import classNames from 'classnames';
import { motion, useAnimation, useReducedMotion } from 'framer-motion'; // import framer-motion
import { useI18n } from '@/libraries/contexts/i18n-context';
import { useEffect } from 'react';
import Icon, { IconSize } from '@/components/atoms/icon/icon';
import Typography from '@/components/atoms/typography/typography';
import styles from './favorite-heart.module.scss';

export interface FavoriteHeartProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  /** The class name to apply */
  className?: string;
  /** Indicates weather the item is a favorite */
  isFavorite?: boolean;
  /** An event that is triggered on click favorite */
  onClickFavorite?: () => void;
  /** Indicates weather to show the label or not */
  showLabel?: boolean;
  /** The size of the heart icon */
  size?: IconSize;
}

const FavoriteHeart = ({
  className,
  disabled,
  isFavorite = false,
  onClickFavorite,
  size = 24,
  showLabel = false,
  ...rest
}: FavoriteHeartProps) => {
  const { t } = useI18n();
  const controls = useAnimation();
  const prefersReducedMotion = useReducedMotion();

  useEffect(() => {
    if (isFavorite && !prefersReducedMotion) {
      controls.start({
        scale: [1, 1.2, 1],
        transition: { duration: 0.3 },
      });
    }
  }, [isFavorite, controls, prefersReducedMotion]);

  return (
    <button
      {...rest}
      aria-label={
        isFavorite
          ? t('items.favorites.alreadyInFavorites')
          : t('items.favorites.addToFavorites')
      }
      className={classNames(styles.component, className, {
        [styles.isDisabled]: disabled,
        [styles.isFavorite]: isFavorite,
      })}
      onClick={event => {
        event.preventDefault();
        event.stopPropagation();
        !disabled && onClickFavorite?.();
      }}
    >
      <motion.div animate={controls} className={styles.iconWrapper}>
        <Icon
          className={styles.favoriteIcon}
          name={isFavorite ? 'HeartFilled' : 'Heart'}
          size={size}
        />
      </motion.div>

      {showLabel && (
        <Typography>
          {isFavorite
            ? t('items.favorites.alreadyInFavorites')
            : t('items.favorites.addToFavorites')}
        </Typography>
      )}
    </button>
  );
};

FavoriteHeart.displayName = 'FavoriteHeart';

export default FavoriteHeart;
